import React, { useState, useRef, useEffect } from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';
import Title from '../../component/title/Title';
import FleetIcon from '../../component/navigation/img/fleetAI.svg';
import './FleetAI.css';

// Use proxy in development to avoid CORS, direct URL in production
const fleetAIBackend = process.env.REACT_APP_FLEET_AI_BACKEND || 'https://horizon-gen-ai-247613415584.asia-southeast1.run.app';
const BACKEND_URL = process.env.NODE_ENV === 'development'
  // ? '/api/fleet-ai/v1/velocity-ai/query'
  // : `${fleetAIBackend}/v1/velocity-ai/query`;
  ? '/api/fleet-ai/v1/horizon-gen-ai/query'
  : `${fleetAIBackend}/v1/horizon-gen-ai/query`;

const QUERY_LIMIT = 1000;
const fleetAIApiKey = process.env.REACT_APP_FLEET_AI_API_KEY;

const quickPrompts = [
  'How many asset with Road asset type?',
  'Generate last week report each device.',
  'List Asset with Road asset type.',
  'Maximum speed for each asset?',
  'Average trip for each asset in one month',
  'Total distances for all asset today?',
];

const FLEET_AI_STORAGE_KEY = 'fleetAI_chatHistory';
const FLEET_AI_CURRENT_CHAT_KEY = 'fleetAI_currentChatId';

const FleetAI = () => {
  const name = Cookies.get('name') || '';
  const firstName = () => (name ? name.split(' ')[0] : '');

  // Load chat history from localStorage on mount
  const loadChatHistory = () => {
    try {
      const savedHistory = localStorage.getItem(FLEET_AI_STORAGE_KEY);
      if (savedHistory) {
        const parsed = JSON.parse(savedHistory);
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading chat history from localStorage:', error);
    }
    return [];
  };

  const loadCurrentChatId = () => {
    try {
      const savedChatId = localStorage.getItem(FLEET_AI_CURRENT_CHAT_KEY);
      return savedChatId ? Number(savedChatId) : null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading current chat ID from localStorage:', error);
      return null;
    }
  };

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [chatHistory, setChatHistory] = useState(loadChatHistory);
  const [currentChatId, setCurrentChatId] = useState(loadCurrentChatId);
  const [searchQuery, setSearchQuery] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const chatContainerRef = useRef(null);

  const formatChatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatAIResponse = (text) => {
    if (!text || typeof text !== 'string') {
      return { title: text || '', body: [] };
    }

    // Remove markdown bold (**)
    const cleanedText = text.replace(/\*\*/g, '');

    // Normalize: ensure numbered sections start on a new line
    // e.g. "... insights. 1. AI Forecasts:" -> "... insights.\n1. AI Forecasts:"
    const normalizedText = cleanedText.replace(/(\.)\s+(\d+\.\s+)/g, '$1\n$2');

    // Check for numbered sections (e.g., "1. AI Forecasts:", "2. Take Action:")
    // Pattern matches: number, period, space, text, colon
    const numberedSectionPattern = /(\d+\.\s+[^:]+:)/g;
    const numberedSectionMatches = [...normalizedText.matchAll(numberedSectionPattern)];

    // If we have numbered sections, split by them
    if (numberedSectionMatches.length > 0) {
      const sections = [];

      numberedSectionMatches.forEach((match, index) => {
        const sectionHeader = match[0];
        const headerIndex = match.index;

        // Get content before first section (intro/title)
        if (index === 0 && headerIndex > 0) {
          const intro = normalizedText.substring(0, headerIndex).trim();
          if (intro.length > 0) {
            sections.push({ type: 'intro', content: intro });
          }
        }

        // Get section content (until next section or end)
        const nextSectionIndex = index < numberedSectionMatches.length - 1
          ? numberedSectionMatches[index + 1].index
          : normalizedText.length;

        const sectionContent = normalizedText.substring(
          headerIndex + sectionHeader.length,
          nextSectionIndex,
        ).trim();

        const headerText = sectionHeader.replace(/^\d+\.\s*/, '').replace(':', '').trim();
        sections.push({
          type: 'section',
          header: headerText,
          content: sectionContent,
        });
      });

      // Format sections
      const title = sections[0]?.type === 'intro' ? sections[0].content : '';
      const body = [];

      sections.forEach((section) => {
        if (section.type === 'section') {
          // Add section header
          body.push(`${section.header}:`);

          // Process section content - split by asterisks (bullets) first
          const bulletItems = section.content.split(/\s*\*\s+/).filter((item) => item.trim().length > 0);

          bulletItems.forEach((item) => {
            const trimmed = item.trim();
            // Remove leading bullet markers if any
            const cleanItem = trimmed.replace(/^[-•*]\s+/, '');
            // Split by newlines to handle multi-line items
            const lines = cleanItem.split('\n').filter((l) => l.trim().length > 0);
            lines.forEach((line) => {
              body.push(`  ${line.trim()}`);
            });
          });
        }
      });

      return { title, body };
    }

    // Split by asterisks (*) to break down items - asterisks are used as separators
    // Pattern: "text * item1 * item2" becomes separate lines
    const asteriskSplit = normalizedText.split(/\s*\*\s*/).filter((item) => item.trim().length > 0);

    // If we have asterisk-separated items, process them
    if (asteriskSplit.length > 1) {
      // First part is usually the main text/title
      const title = asteriskSplit[0].trim();

      // Rest are the bullet items
      const body = asteriskSplit.slice(1).map((item) => {
        const trimmed = item.trim();
        // Format items with colons - put content after colon on new line
        if (trimmed.includes(':')) {
          const colonIndex = trimmed.indexOf(':');
          const label = trimmed.substring(0, colonIndex).trim();
          const content = trimmed.substring(colonIndex + 1).trim();
          return `${label}:\n  ${content}`;
        }
        return trimmed;
      });

      // Process body to handle multi-line items
      const processedBody = [];
      body.forEach((item) => {
        const lines = item.split('\n');
        lines.forEach((line) => {
          if (line.trim().length > 0) {
            processedBody.push(line);
          }
        });
      });

      return { title, body: processedBody };
    }

    // Fallback: original sentence-based formatting
    // Improve readability - insert line breaks after periods
    const formattedText = normalizedText.replace(/\. /g, '.\n');

    // Split into sentences
    const sentences = formattedText.split(/(?<=[.?!])\s+/).filter((s) => s.trim());

    if (sentences.length === 0) {
      return { title: normalizedText, body: [] };
    }

    // First sentence as title
    const title = sentences[0].trim();

    // Rest as body
    const body = sentences.slice(1).map((s) => s.trim()).filter((s) => s.length > 0);

    return { title, body };
  };

  const detectNoData = (text) => {
    if (!text || typeof text !== 'string') return false;
    const lowerText = text.toLowerCase();
    return (
      lowerText.includes('no data')
      || lowerText.includes('cannot determine')
      || lowerText.includes('not available')
      || lowerText.includes('no information')
      || lowerText.includes('unable to find')
    );
  };

  const createNewChat = () => {
    const newChatId = Date.now();
    const newChat = {
      id: newChatId,
      title: 'New Chat',
      messages: [],
      createdAt: new Date().toISOString(),
    };
    setChatHistory((prev) => [newChat, ...prev]);
    setCurrentChatId(newChatId);
    setChatMessages([]);
    setInputValue('');
    // Save current chat ID to localStorage
    try {
      localStorage.setItem(FLEET_AI_CURRENT_CHAT_KEY, String(newChatId));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error saving current chat ID to localStorage:', error);
    }
  };

  const selectChat = (chatId) => {
    const chat = chatHistory.find((c) => c.id === chatId);
    if (chat) {
      setCurrentChatId(chatId);
      setChatMessages(chat.messages);
      // Save current chat ID to localStorage
      try {
        localStorage.setItem(FLEET_AI_CURRENT_CHAT_KEY, String(chatId));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error saving current chat ID to localStorage:', error);
      }
    }
  };

  const deleteChat = (chatId, event) => {
    event.stopPropagation();
    setChatHistory((prev) => prev.filter((c) => c.id !== chatId));
    if (currentChatId === chatId) {
      const remainingChats = chatHistory.filter((c) => c.id !== chatId);
      if (remainingChats.length > 0) {
        const nextChat = remainingChats[0];
        setCurrentChatId(nextChat.id);
        setChatMessages(nextChat.messages);
        try {
          localStorage.setItem(FLEET_AI_CURRENT_CHAT_KEY, String(nextChat.id));
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error saving current chat ID to localStorage:', error);
        }
      } else {
        setCurrentChatId(null);
        setChatMessages([]);
        try {
          localStorage.removeItem(FLEET_AI_CURRENT_CHAT_KEY);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error removing current chat ID from localStorage:', error);
        }
      }
    }
  };

  const sendMessage = async (text) => {
    if (!text.trim()) return;

    const storedCount = Number(localStorage.getItem('fleetAIQueryCount') || '0');
    const safeCount = Number.isNaN(storedCount) ? 0 : storedCount;
    if (safeCount >= QUERY_LIMIT) {
      return;
    }
    localStorage.setItem('fleetAIQueryCount', String(safeCount + 1));

    const timestamp = Date.now();
    const userMessage = { id: timestamp, text, isUser: true };
    const loadingBotMessage = {
      id: timestamp + 1,
      text: '...',
      isUser: false,
      isLoading: true,
    };

    // Add user message and loading bot message immediately
    if (!currentChatId) {
      const newChatId = timestamp;
      const newChat = {
        id: newChatId,
        title: text.substring(0, 50) || 'New Chat',
        messages: [userMessage, loadingBotMessage],
        createdAt: new Date().toISOString(),
      };
      setChatHistory((prev) => [newChat, ...prev]);
      setCurrentChatId(newChatId);
      setChatMessages([userMessage, loadingBotMessage]);
    } else {
      setChatHistory((prev) => prev.map((chat) => (
        chat.id === currentChatId
          ? {
              ...chat,
              title:
                chat.title === 'New Chat' && chat.messages.length === 0
                  ? text.substring(0, 50)
                  : chat.title,
              messages: [...chat.messages, userMessage, loadingBotMessage],
            }
          : chat
      )));
      setChatMessages((prev) => [...prev, userMessage, loadingBotMessage]);
    }

    setInputValue('');
    setIsLoading(true);

    // Call the API
    try {
      const token = Cookies.get('jwtToken');
      const agencyId = Cookies.get('agencyUUID') || Cookies.get('agency-id');

      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      if (!agencyId) {
        throw new Error('Agency ID is missing. Please select an agency.');
      }

      const response = await axios.post(
        BACKEND_URL,
        { question: text },
        {
          headers: {
            Authorization: token,
            'x-api-key': fleetAIApiKey,
            'agency-id': agencyId,
            'Content-Type': 'application/json',
          },
          timeout: 30000, // 30 second timeout
        },
      );

      // Extract response text from API response
      let responseText = 'Thank you for using Fleet AI';
      if (response.data) {
        // Handle different possible response structures
        if (typeof response.data === 'string') {
          responseText = response.data;
        } else if (response.data.data) {
          // Backend returns { status: "success", data: "response text" }
          responseText = typeof response.data.data === 'string'
            ? response.data.data
            : JSON.stringify(response.data.data);
        } else if (response.data.response) {
          responseText = response.data.response;
        } else if (response.data.message) {
          responseText = response.data.message;
        } else if (response.data.text) {
          responseText = response.data.text;
        } else if (response.data.summary || response.data.insights || response.data.dba_analysis || response.data.driver_info) {
          responseText = [
            response.data.summary,
            response.data.insights,
            response.data.dba_analysis,
            response.data.driver_info].filter(Boolean).join('\n\n');
        }
      }

      const botMessage = {
        id: timestamp + 1,
        text: responseText,
        isUser: false,
        isLoading: false,
      };

      // Update the chat with the actual response
      setChatHistory((prev) => prev.map((chat) => (
        chat.id === currentChatId || chat.id === timestamp
          ? {
              ...chat,
              messages: chat.messages.map((msg) => (
                msg.id === timestamp + 1 ? botMessage : msg
              )),
            }
          : chat
      )));
      setChatMessages((prev) => prev.map((msg) => (
        msg.id === timestamp + 1 ? botMessage : msg
      )));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error calling Fleet AI API:', error);
      // eslint-disable-next-line no-console
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        status: error.response?.status,
        request: error.request,
      });

      let errorText = 'Sorry, I encountered an error. Please try again.';

      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        errorText = error.response.data?.message
          || error.response.data?.error
          || `Server error: ${error.response.status} ${error.response.statusText}`;
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorText = 'Request timeout: The server took too long to respond. Please try again.';
      } else if (error.request) {
        // The request was made but no response was received
        // This could be CORS, network issue, or server down
        if (error.message?.includes('CORS') || error.message?.includes('Network Error')) {
          errorText = 'CORS error: The server may not allow requests from this origin. Please contact support.';
        } else {
          errorText = 'Network error: Unable to reach the server. Please check your connection and try again.';
        }
      } else {
        // Something happened in setting up the request that triggered an Error
        errorText = error.message || 'An unexpected error occurred.';
      }

      const errorMessage = {
        id: timestamp + 1,
        text: errorText,
        isUser: false,
        isLoading: false,
      };

      // Update the chat with error message
      setChatHistory((prev) => prev.map((chat) => (
        chat.id === currentChatId || chat.id === timestamp
          ? {
              ...chat,
              messages: chat.messages.map((msg) => (
                msg.id === timestamp + 1 ? errorMessage : msg
              )),
            }
          : chat
      )));
      setChatMessages((prev) => prev.map((msg) => (
        msg.id === timestamp + 1 ? errorMessage : msg
      )));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = () => {
    sendMessage(inputValue);
  };

  const handlePromptClick = (prompt) => {
    sendMessage(prompt);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  // Save chat history to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(FLEET_AI_STORAGE_KEY, JSON.stringify(chatHistory));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error saving chat history to localStorage:', error);
    }
  }, [chatHistory]);

  // Save current chat ID to localStorage whenever it changes
  useEffect(() => {
    try {
      if (currentChatId !== null) {
        localStorage.setItem(FLEET_AI_CURRENT_CHAT_KEY, String(currentChatId));
      } else {
        localStorage.removeItem(FLEET_AI_CURRENT_CHAT_KEY);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error saving current chat ID to localStorage:', error);
    }
  }, [currentChatId]);

  // Restore current chat messages when component mounts or currentChatId changes
  useEffect(() => {
    if (currentChatId !== null) {
      const chat = chatHistory.find((c) => c.id === currentChatId);
      if (chat) {
        setChatMessages(chat.messages);
      } else {
        // If chat not found, clear current chat ID
        setCurrentChatId(null);
        setChatMessages([]);
      }
    } else {
      setChatMessages([]);
    }
  }, [currentChatId, chatHistory]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  const filteredHistory = chatHistory.filter((chat) => (
    chat.title.toLowerCase().includes(searchQuery.toLowerCase())
    || chat.messages.some((msg) => msg.text.toLowerCase().includes(searchQuery.toLowerCase()))
  ));

  const isEmptyState = chatMessages.length === 0;
  const shouldShowQuickPrompts = isEmptyState;

  return (
    <div className="fleetAI-container">
      <div className="fleetAI-page-header">
        <div className="fleetAI-breadcrumb" />
        <div className="fleetAI-heading">
          <Title title={`Welcome back, ${name && firstName()}`} subtitle="Here is our assistance for your question" titleSize="1.875rem" />
        </div>
      </div>

      <section className="fleetAI-card fleetAI-main-card">
        <div className="fleetAI-main-layout">
          <aside className={`fleetAI-sidebar ${sidebarOpen ? 'is-open' : 'is-collapsed'}`}>
          <div className="fleetAI-sidebar-header">
            <button
              type="button"
              className="fleetAI-sidebar-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                {sidebarOpen ? (
                  <path
                    d="M9 18L15 12L9 6"
                    stroke="#475467"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                ) : (
                  <path
                    d="M15 18L9 12L15 6"
                    stroke="#475467"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                )}
              </svg>
            </button>
            {sidebarOpen && (
              <>
                <button
                  type="button"
                  className="fleetAI-new-chat-button"
                  onClick={createNewChat}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 5V19M5 12H19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  New Chat
                </button>
              </>
            )}

          </div>
          {sidebarOpen && (
            <>
              <div className="fleetAI-sidebar-search">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                    stroke="#98A2B3"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                <input
                  type="text"
                  placeholder="Search history..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="fleetAI-sidebar-content">
                {filteredHistory.length === 0 ? (
                  <div className="fleetAI-sidebar-empty">
                    <p>No chat history</p>
                    <p className="fleetAI-sidebar-empty-sub">Start a new conversation</p>
                  </div>
                ) : (
                  <div className="fleetAI-history-list">
                    {filteredHistory.map((chat) => (
                      <div
                        key={chat.id}
                        className={`fleetAI-history-item ${currentChatId === chat.id ? 'is-active' : ''}`}
                        onClick={() => selectChat(chat.id)}
                        role="button"
                        tabIndex={0}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            selectChat(chat.id);
                          }
                        }}
                      >
                        <div className="fleetAI-history-item-content">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M8 10H16M8 14H16M6 20H18C19.1046 20 20 19.1046 20 18V6C20 4.89543 19.1046 4 18 4H6C4.89543 4 4 4.89543 4 6V18C4 19.1046 4.89543 20 6 20Z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                          </svg>
                          <div className="fleetAI-history-item-text">
                            <span className="fleetAI-history-item-title">{chat.title}</span>
                            <span className="fleetAI-history-item-date">{formatChatDate(chat.createdAt)}</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="fleetAI-history-item-delete"
                          onClick={(e) => deleteChat(chat.id, e)}
                          aria-label="Delete chat"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M18 6L6 18M6 6L18 18"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
          </aside>

        {/* Main Chat Area */}
        <section className="fleetAI-chat-card">
          <div className="fleetAI-chat-header">
            <div className="fleetAI-chat-header-content">
              <img src={FleetIcon} alt="Fleet AI" className="fleetAI-chat-icon" />
              <div>
                <p className="fleetAI-chat-title">HORIZON FLEET AI</p>
                <p className="fleetAI-chat-support">Ask a question to get instant insights</p>
              </div>
            </div>
          </div>

          <div className="fleetAI-chat-messages" ref={chatContainerRef}>
            {isEmptyState && (
              <div className="fleetAI-chat-empty-state">
                <img src={FleetIcon} alt="Fleet AI" className="fleetAI-empty-icon" />
                <p className="fleetAI-empty-title">Start a conversation</p>
                <p className="fleetAI-empty-subtitle">Ask a question to get instant insights</p>
              </div>
            )}

            {chatMessages.map((message) => {
              const renderMessageContent = () => {
                if (message.isUser) {
                  return message.text;
                }
                if (message.isLoading) {
                  return (
                    <div className="fleetAI-loading-dots">
                      <span className="fleetAI-dot">.</span>
                      <span className="fleetAI-dot">.</span>
                      <span className="fleetAI-dot">.</span>
                    </div>
                  );
                }
                const { title, body } = formatAIResponse(message.text);
                const isNoData = detectNoData(message.text);
                return (
                  <div className="fleetAI-ai-response">
                    <div className="fleetAI-ai-title">{title}</div>
                    {body.length > 0 && (
                      <ul className="fleetAI-ai-explanation">
                        {body.map((line) => {
                          // Create a unique key using message ID and first 30 chars of line
                          const lineHash = line.substring(0, 30).replace(/\s/g, '-');
                          const lineKey = `${message.id}-${lineHash}`;
                          // Check if line starts with spaces (indented content after colon)
                          const isIndented = line.startsWith('  ');
                          const displayLine = isIndented ? line.trim() : line;
                          return (
                            <li key={lineKey} className={isIndented ? 'fleetAI-ai-indented' : ''}>
                              {displayLine}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    {isNoData && (
                      <div className="fleetAI-ai-next-step">
                        ⚠️ Data not available for this request.
                      </div>
                    )}
                  </div>
                );
              };

              return (
                <div
                  key={message.id}
                  className={`fleetAI-message ${message.isUser ? 'fleetAI-message-user' : 'fleetAI-message-ai'}`}
                >
                  {renderMessageContent()}
                </div>
              );
            })}
          </div>

          {shouldShowQuickPrompts && (
            <div className="fleetAI-quick-prompts">
              {quickPrompts.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  className="fleetAI-quick-prompt"
                  onClick={() => handlePromptClick(prompt)}
                >
                  <span aria-hidden="true">→</span>
                  {prompt}
                </button>
              ))}
            </div>
          )}

          <div className="fleetAI-chat-input-row">
            {/* <button type="button" className="fleetAI-attach-button" aria-label="Attach file">
              <span className="fleetAI-attach-icon">+</span>
            </button> */}
            <input
              type="text"
              className="fleetAI-chat-input"
              placeholder="Ask Fleet AI..."
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              onKeyPress={handleKeyPress}
            />
            <button
              type="button"
              className="fleetAI-send-button"
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
              aria-label="Send message"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M2.5 10L17.5 10M17.5 10L11.6667 3.33333M17.5 10L11.6667 16.6667"
                  stroke="#FFFFFF"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </section>
        </div>
      </section>
    </div>
  );
};

export default FleetAI;
